/**
 * Internal-link cluster for service pillar pages (Thai).
 * Every pillar page gets: (1) topical hub links inside its own cluster,
 * (2) evergreen knowledge-base links, (3) same-category sibling services.
 * Purely presentational — link targets are static, verified routes.
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

type Cat = "notary" | "translation" | "legalization" | "background" | "visa-marriage";

type Item = { to: string; label: string; hint: string };

const HUBS: Record<Cat, Item[]> = {
  notary: [
    { to: "/notary", label: "ศูนย์ข้อมูล Notary Public", hint: "ภาพรวมงานรับรองโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร" },
    { to: "/notary/faq", label: "คำถามที่พบบ่อย — Notary Public", hint: "รวมคำถามจริงจากลูกค้า แยกตามหมวด" },
    { to: "/poa/faq", label: "หนังสือมอบอำนาจ (POA)", hint: "แบบฟอร์ม ท.ด.21 อากรแสตมป์ และการใช้ต่างประเทศ" },
    { to: "/consular-legalization/faq", label: "นิติกรณ์กงสุล / Apostille", hint: "ขั้นตอนต่อจาก Notary เพื่อใช้ในต่างประเทศ" },
  ],
  translation: [
    { to: "/translation", label: "ศูนย์ข้อมูลงานแปลรับรอง", hint: "ทุกภาษา ทุกชนิดเอกสาร และการรับรองคำแปล" },
    { to: "/translation/faq", label: "คำถามที่พบบ่อย — งานแปล", hint: "รับรองคำแปล กรมการกงสุล นักแปล MoJ" },
    { to: "/naati/faq", label: "NAATI (ออสเตรเลีย / นิวซีแลนด์)", hint: "คำแปลที่หน่วยงานออสเตรเลียยอมรับ" },
    { to: "/interpreter/faq", label: "ล่ามราชการและล่ามศาล", hint: "ล่ามงานทะเบียน ศาล ที่ดิน และ ตม." },
  ],
  legalization: [
    { to: "/consular-legalization/faq", label: "ศูนย์ข้อมูลนิติกรณ์กงสุล", hint: "กองสัญชาติและนิติกรณ์ กรมการกงสุล" },
    { to: "/embassies", label: "ไดเรกทอรีสถานทูต/สถานกงสุล", hint: "ข้อกำหนดรับรองเอกสารแยกตามประเทศ" },
    { to: "/translation/faq", label: "รับรองคำแปลก่อนยื่นกงสุล", hint: "ขั้นตอนที่ต้องทำก่อนนิติกรณ์" },
    { to: "/corporate-docs/faq", label: "เอกสารนิติบุคคล", hint: "หนังสือรับรอง บอจ. งบการเงิน เพื่อใช้ต่างประเทศ" },
  ],
  background: [
    { to: "/police-clearance", label: "ศูนย์ข้อมูลตรวจประวัติอาชญากรรม", hint: "หนังสือรับรองความประพฤติ (PCC)" },
    { to: "/police-clearance/faq", label: "คำถามที่พบบ่อย — ตรวจประวัติ", hint: "เอกสาร ระยะเวลา และการยื่นจากต่างประเทศ" },
    { to: "/consular-legalization/faq", label: "รับรองกงสุลสำหรับ PCC", hint: "ขั้นตอนต่อเนื่องเพื่อใช้ต่างประเทศ" },
    { to: "/employment/faq", label: "เอกสารสมัครงาน/ทุนต่างประเทศ", hint: "ชุดเอกสารที่มักขอ PCC ร่วมด้วย" },
  ],
  "visa-marriage": [
    { to: "/visa/faq", label: "ศูนย์ข้อมูลวีซ่าและตรวจคนเข้าเมือง", hint: "วีซ่าไทยและวีซ่าต่างประเทศ" },
    { to: "/marriage/faq", label: "ศูนย์ข้อมูลจดทะเบียนสมรส", hint: "ในไทยและต่างประเทศ พร้อมเอกสารที่ต้องใช้" },
    { to: "/work-permit/faq", label: "ใบอนุญาตทำงาน", hint: "กรมการจัดหางาน และเงื่อนไขนายจ้าง" },
    { to: "/civil-registry/faq", label: "ทะเบียนราษฎรและทะเบียนครอบครัว", hint: "คร.2 คร.22 และการบันทึกฐานะแห่งครอบครัว" },
  ],
};

const KNOWLEDGE: Item[] = [
  { to: "/catalog", label: "แคตตาล็อกบริการทั้งหมด", hint: "ค้นหาบริการทุกรายการในที่เดียว" },
  { to: "/insights", label: "บทความเชิงลึก", hint: "คู่มืออัปเดตตามกฎระเบียบล่าสุด" },
  { to: "/howto", label: "คู่มือทำตามขั้นตอน (How-to)", hint: "ทำเองได้ทีละขั้น พร้อมเอกสารที่ต้องเตรียม" },
  { to: "/glossary", label: "คลังศัพท์กฎหมาย/เอกสาร", hint: "นิยามคำที่ใช้ในงานรับรองเอกสาร" },
  { to: "/compare", label: "หน้าเปรียบเทียบตัวเลือก", hint: "เลือกวิธีที่เหมาะกับกรณีของคุณ" },
  { to: "/contact", label: "ติดต่อเจ้าหน้าที่", hint: "สอบถามค่าบริการทางโทรศัพท์ / LINE / อีเมล" },
];

export function ServiceKnowledgeLinks({
  category,
  siblings = [],
}: {
  category: string;
  siblings?: { slug: string; title: string }[];
}) {
  const hubs = HUBS[(category as Cat) in HUBS ? (category as Cat) : "notary"];
  return (
    <>
      <section aria-label="ศูนย์ข้อมูลที่เกี่ยวข้อง">
        <h2 className="text-2xl font-black text-primary md:text-3xl">ศูนย์ข้อมูลที่เกี่ยวข้องกับบริการนี้</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          อ่านต่อในคลัสเตอร์เดียวกันเพื่อดูเอกสาร ขั้นตอน และคำถามที่พบบ่อยแบบละเอียด
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {hubs.map((h) => (
            <Link
              key={h.to}
              to={h.to}
              className="group rounded-xl border border-border bg-card p-4 hover:border-gold"
            >
              <h3 className="text-sm font-bold text-primary group-hover:text-gold">{h.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{h.hint}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-gold">
                อ่านต่อ <ChevronRight className="h-3 w-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {siblings.length > 0 && (
        <section aria-label="บริการอื่นในหมวดเดียวกัน">
          <h2 className="text-2xl font-black text-primary md:text-3xl">บริการอื่นในหมวดเดียวกัน</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to="/services/$service"
                params={{ service: s.slug }}
                className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold text-primary hover:border-gold hover:text-gold"
              >
                {s.title}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section aria-label="คลังความรู้">
        <h2 className="text-2xl font-black text-primary md:text-3xl">คลังความรู้และเครื่องมือ</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {KNOWLEDGE.map((k) => (
            <Link key={k.to} to={k.to} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
              <h3 className="text-sm font-bold text-primary group-hover:text-gold">{k.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
